import declare from "dojo/_base/declare";
import config from "csi/config";
import def from "csi/def";
import enums from "csi/enums";
import Widget from "csi/form/Widget";
import prefs from "csi/prefs";
import reflection from "csi/reflection";
import resolver from "csi/resolver";
import csiStore from "csi/store/_base";
import xml from "csi/xml";

import { Field } from "csi/def/Field";
import { View } from "csi/def/View";
import { StoreNames } from "csi/store/Names";

const LATEST_ONLY = "LatestOnly";
const NESTED_SHEET_PARENT_TYPE = "NestedSheetParentType";
const PHASE = "Phase";
const STATE = "State";
const SPEC_DATASHEET = "SpecificationDataSheet";
const SUB_TYPE = "SubType";
const SUBTYPE = "Subtype";
const TYPE = "Type";
const VIEW = "View";

const FOR_PARENT = "forParent";

const DataPackageTemplateSheet = declare(Widget, {
	_bHidePhase: false,
	_bHideState: false,
	_bHideSubType: false,
	_bHideSubtype: false,
	_bHideView: false,

	_requiredExpressions: {
		Subtype: "attr('" + TYPE + "') == 'DataPackageSheetType:SpecificationDataSheet'",
	},

	/**
	 * For ShapeSizeChart and ShapeSizeChartReview remove "Shape" from type
	 */
	_removeShapeFromType(type: string) {
		return type.startsWith("Shape") ? type.replace("Shape", "") : type;
	},

	/**
	 * @override
	 * Return field stores for DataPackageTemplateSheet for product type of DataPackage.
	 * The available DataPackageSheetSubtypes to be selected is based on the value of DataPackageSheetType.
	 */
	getFieldStore(field: Field, value: string): StoreNames {
		let templateType = xml.enumGetText(this.defView.getFieldValue(NESTED_SHEET_PARENT_TYPE));
		const type = this._removeShapeFromType(xml.enumGetText(this.defView.getFieldValue(TYPE)));
		const name = field.getControlName();
		let store: StoreNames;

		if (name == TYPE) {
			// filter out data sheets based on product type
			const productType = xml.enumGetText(this.defView.m_formData.getParent().TemplateType);
			const rc = reflection.getClass(productType);

			store = enums.getStore(field.getEnumId(value), null, (enumValue: string) => {
				const type = this._removeShapeFromType(xml.enumGetText(enumValue));
				if (("Artwork" === type) && ["FormulatedMaterial", "Material"].includes(productType)) {
					return false;
				}

				if (("FoodLabelDataSheet" === type || "PrintDesign" === type) && ["Style", "Material"].includes(productType)) {
					return true;
				}

				if (("ApparelBOM" === type) && ["FormulatedMaterial", "Food", "Material", "FoodSKU"].includes(productType)) {
					return false;
				} 
				
				if (("StyleBOM" === type) && !["FormulatedMaterial", "Food", "Material", "FoodSKU"].includes(productType)) {
					return true;
				}

				let typeParent = reflection.getTypeAttributeDestination(type, xml.ATTR_PARENT)?.type;

				if (typeParent === "Colorway") {
					typeParent = "Style";
				}

				if (this.defView.classControllerParams && this.defView.classControllerParams[FOR_PARENT]) {
					return rc.getParentClass().isDerivedFrom(typeParent);
				}

				if (productType == "FoodSKU") {
					if (type === "ReviewFood" || type === "Routing") {
						return false;
					}
					return rc.isDerivedFrom(typeParent) || rc.getParentClass().isDerivedFrom(typeParent);
				}

				return rc.isDerivedFrom(typeParent);
			});

			// Force localization of DataPackageSheetType enum values
			/*store._store.data = store._store.data.map(item => {
				item.name = locale.type(xml.enumGetText(item.id));
				return item;
			})*/
		} else if (name == STATE) {
			// restrict selection for SizeChartReview to only APPROVED
			this._bHideState = !type;

			store = enums.getStore(field.getEnumId(value), null, (enumValue) => {
				return (enumValue != "DataSheetState:PENDING") || ((type != "PrintDesign" && type != "SizeChartReview"));
			});
		} else if (name == SUB_TYPE) {
			// hide subtype if not any for type
			const rc = reflection.getClass(type);
			const rca = rc ? rc.getAttribute(SUB_TYPE, true) : null;
			this._bHideSubType = !rca;
			store = enums.getStore(rca ? xml.enumGetId(rca.getValue()) : field.getEnumId());
		} else if (field.m_id == SUBTYPE && type === SPEC_DATASHEET) {
			if (!templateType) {
				templateType = xml.enumGetText(this.defView.m_formData.getParent().TemplateType);
			}
			const productType = templateType === "FoodSKU" ? "Food" : templateType;
			const applicableForTypesEnum = productType ? xml.enumCreate("Spec.ApplicableForTypes", productType) : null;
			if (applicableForTypesEnum) {
				store = config.getSubtypeStore(SPEC_DATASHEET, (subtypeSettings) => {
					const applicableTypes = subtypeSettings.ApplicableForTypes;
					if (applicableTypes) {
						return applicableTypes.includes(applicableForTypesEnum);
					}
					return true;	//if applicableTypes is undefined, spec type applies to all subtypes
				});
			}
			this._bHideSubtype = !store;
		} else if (field.m_id == SUBTYPE) {
			// build subtype field based on current data-sheet type
			store = type ? config.getSubtypeStore(type) : null;
			this._bHideSubtype = !store;
		} else if (name == VIEW) {
			// get view store from data sheet TDS view
			const cc = reflection.getClassConfig(type);
			const urlSubtype = this._bHideSubtype ? null : this.defView.getFieldValue(SUBTYPE);
			const subtype = xml.isValidURL(urlSubtype) && cc.isSiloCustomViewsBySubtype() ? resolver.resolveNode(urlSubtype, true) : null;
			const keyView = def.getPdfViewKey(type, subtype ? subtype.$Name : null);
			field.m_relationship = prefs.getStoreRelationship(keyView);
			this._bHideView = !keyView;
		}

		store = store || this.inherited<StoreNames>(arguments);

		if (name == PHASE) {
			// get phases from data sheet type
			const cc = reflection.getClassConfig(type);
			const phases = cc?.getPhases();

			store.bSort = false;

			phases?.forEach((p) => {
				store.put(csiStore.nameObject(p));
			});

			this._bHidePhase = !phases;
		}

		return store;
	},

	/**
	 * Reset numbers
	 */
	_redraw() {
		this._bHidePhase = false;
		this._bHideState = false;
		this._bHideSubType = false;
		this._bHideSubtype = false;
		this._bHideView = false;
		this.inherited(arguments);
	},

	/**
	 * Add onChange behavior to fields
	 */
	_resolveView() {
		this.defView.getField(SUBTYPE).m_onChange = "_onChangeSubtype";
		this.inherited(arguments);
	},

	/**
	 * View actually has two dependencies - Type and (SpecificationDataSheet) Subtype
	 */
	_onChangeSubtype(defView: View, field: Field, value: any) {
		this._onDependency(defView.getField(VIEW), field);
	},

	/**
	 * Set visibility of fields
	 */
	_updateFieldVisibility(field?: Field) {
		const b = this.inherited(arguments);
		const type = xml.enumGetText(this.defView.getFieldValue(TYPE));

		if (!field || (field.m_id == TYPE)) {
			this.setFieldHidden(PHASE, this._bHidePhase);
			this.setFieldHidden(SUB_TYPE, this._bHideSubType);
			this.setFieldHidden(SUBTYPE, this._bHideSubtype);
			// C8-18069, Hiding the State Field for the current release 7.0, Will be enabled in future.
			if (type == "PrintDesign") {
				this.setFieldHidden(STATE, true);
				this.setFieldHidden(LATEST_ONLY, true);
			} else {
				this.setFieldHidden(STATE, this._bHideState);
				this.setFieldHidden(LATEST_ONLY, !type);
			}
			this.setFieldHidden(VIEW, this._bHideView);
			return true;
		}

		return b;
	},
});

export = DataPackageTemplateSheet